import {
  AppContext,
  CancelRider,
  CancelRiderFromInceptionService,
  CancelRiderService,
  Checkbox,
  ErrorHandling,
  formatNumberWithComma,
  Panel,
  RoundedButton,
  Title,
  TransactionType
} from '@pulseops/common'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { View, SafeAreaView, Text, StyleSheet } from 'react-native'
import { pipe } from 'fp-ts/lib/function'
import { ZIO } from '@mxt/zio'
import * as O from 'fp-ts/lib/Option'
import { PolicyServiceProps } from '../policy-service-props'
import { Controller, FieldArrayWithId, useFieldArray, useForm } from 'react-hook-form'
import { CancelRiderFormData } from './cancel-rider.form'
import { useLoading } from '@mxt/zio-react'
import { useIsFocused } from '@react-navigation/native'
import { Error } from '@pulseops/submission/common'
import moment from 'moment'
import { RequestAuthenticateData } from '../../request-authen'

export const CancelRiderScreen: React.FC<PolicyServiceProps<CancelRider.SubmitCancelRiderData>> = (props) => {
  const { t, i18n } = useTranslation()
  const policyNum = props.policyNumber ?? ''

  const isFocused = useIsFocused()
  const [isLoading, bindLoader] = useLoading(false)
  const { showGlobalLoading, showToast } = React.useContext(AppContext.AppContextInstance)
  const [detailData, setDetailData] = React.useState<CancelRider.DetailCancelRiderInfo>()
  const [estimatedPremiumFlag, setEstimatedPremiumFlag] = React.useState<boolean>(false)
  const [errorMsg, setErrorMsg] = React.useState<string>('')
  const [customerReceivedDateOfRider, setCustomerReceivedDateOfRider] = React.useState<CancelRiderFromInceptionService.CustomerReceiveDateOfRiderResponse[]>([])
  const [effectiveDateDropRider, setEffectiveDateDropRider] = React.useState<string>('')
  const cancelRiderForm = useForm<CancelRiderFormData.CancelAllRiderForm>({
    defaultValues: {
      cancelAllRider: false,
      LARiderForm: [],
      reEstimatedInstallPremium: 0
    }
  })
  const cancelRiderFormArray = useFieldArray({
    control: cancelRiderForm.control,
    name: 'LARiderForm'
  })

  React.useEffect(() => {
    if (isFocused && policyNum) {
      pipe(
        ZIO.zipPar(
          CancelRiderService.getDetail(policyNum),
          CancelRiderFromInceptionService.getCustomerReceivedDateOfRider(policyNum),
          CancelRiderFromInceptionService.getEffectiveDateDropRiderOfProduct(policyNum)
        ),
        ZIO.map(([responseData, receivedDatesOfRider, effectiveDateDropRiderOfProduct]) => {
          let LARiderFormArray: Array<CancelRiderFormData.RiderForm> = []
          setDetailData(responseData)
          setCustomerReceivedDateOfRider(receivedDatesOfRider)
          setEffectiveDateDropRider(effectiveDateDropRiderOfProduct)
          const LARiderItemList = responseData.laRiderInfo
          LARiderItemList.map((LAItem) => {
            LAItem.riders.map((rider) => {
              const riderInfoItem = responseData.riderNameList.find((x) => x.code === rider.productCode)
              rider.productName = getRiderName(riderInfoItem, i18n.language)
              LARiderFormArray.push({
                riderCode: rider.productCode,
                riderName: rider.productName,
                SelectRowForRider: false,
                riskCommDate: rider.riskCommDate,
                riskCessDate: rider.riskCessDate,
                sumAssured: rider.sumAssured,
                installmentPrem: rider.installmentPrem,
                newInstallmentPremium: 0,
                lifeNo: rider.lifeNo,
                coverageNo: rider.coverageNo,
                riderNo: rider.riderNo,
                riderStatus: rider.riderStatus
              })
            })
            cancelRiderFormArray.append({
              selectAllForLA: false,
              LAName: LAItem.clientName,
              lifeAssured: LAItem.lifeAssured,
              cancelRiderForm: LARiderFormArray
            })
            LARiderFormArray = []
          })
        }),
        bindLoader,
        ErrorHandling.run()
      )
    }
    return () => {
      cancelRiderFormArray.remove()
      setDetailData(undefined)
      setEstimatedPremiumFlag(false)
      setErrorMsg('')
      setCustomerReceivedDateOfRider([])
      setEffectiveDateDropRider('')
      cancelRiderForm.reset({
        cancelAllRider: false,
        LARiderForm: [],
        reEstimatedInstallPremium: 0
      })
    }
  }, [isFocused])

  React.useEffect(() => {
    showGlobalLoading(isLoading)
  }, [isLoading])

  const getRiderName = (item: any, isEN = 'en') => {
    return !!item ? (isEN === 'en' ? item.name : item.nameVi) : ''
  }

  const convertDateStringForSubmit = (strDate: string) => {
    return moment(strDate).format('YYYY-MM-DD')
  }

  const convertNumberToCurrencyFormat = (value: number) => {
    return formatNumberWithComma(value)
  }

  const convertStringToDateString = (strDate: string) => {
    return moment(strDate).format('DD/MM/YYYY')
  }

  const getTotalSelectedPremium = () => {
    let totalPremium: number = 0
    cancelRiderFormArray.fields.forEach((fieldItem) => {
      const riderTotal = fieldItem.cancelRiderForm.reduce((total, riderItem) => {
        return riderItem.SelectRowForRider ? total + riderItem.installmentPrem : total
      }, 0)
      totalPremium += riderTotal
    })
    console.log('totalSelectedInstallPremium: ' + totalPremium)
    return totalPremium
  }

  // only calculate one time when user click first time
  const calculateEstimatedInstallPremium = () => {
    let totalSelectedInstallPremium: number = getTotalSelectedPremium()
    setEstimatedPremiumFlag(true)
    const estimatedPremium = (detailData?.totalPremium ?? 0) - totalSelectedInstallPremium
    cancelRiderForm.setValue('reEstimatedInstallPremium', estimatedPremium)
    if (estimatedPremium < (detailData?.minPremium ?? 0)) {
      // showToast(t(`message:MS050014`), 'error')
      showToast(t(`message:MS050054`, { min: formatNumberWithComma(detailData?.minPremium ?? 0) }), 'error')
      cancelRiderForm.setValue('reEstimatedInstallPremium', 0)
    } else if (estimatedPremium <= 0) {
      showToast(t(`message:MS050034`), 'error')
      cancelRiderForm.setValue('reEstimatedInstallPremium', 0)
    }
  }

  //calculate again when user checked or unchecked rider
  const reCalculateEstimatedInstallPremium = (riderPremium: number, selectedRow: boolean) => {
    let reEstimatedInstallPremiumVal: number = 0
    let totalSelectedInstallPremium: number = 0
    if (estimatedPremiumFlag) {
      const ReEstimatePremium = cancelRiderForm.watch('reEstimatedInstallPremium')
      totalSelectedInstallPremium = Number(detailData?.totalPremium ?? 0) - ReEstimatePremium
      if (selectedRow) {
        reEstimatedInstallPremiumVal =
          Number(detailData?.totalPremium ?? 0) - (totalSelectedInstallPremium + riderPremium)
        cancelRiderForm.setValue('reEstimatedInstallPremium', reEstimatedInstallPremiumVal)
      } else {
        reEstimatedInstallPremiumVal =
          Number(detailData?.totalPremium ?? 0) - (totalSelectedInstallPremium - riderPremium)
        cancelRiderForm.setValue('reEstimatedInstallPremium', reEstimatedInstallPremiumVal)
      }
      if (reEstimatedInstallPremiumVal < Number(detailData?.minPremium)) {
        // showToast(t(`message:MS050014`), 'error')
        showToast(t(`message:MS050054`, { min: formatNumberWithComma(detailData?.minPremium ?? 0) }), 'error')
        cancelRiderForm.setValue('reEstimatedInstallPremium', 0)
      } else if (reEstimatedInstallPremiumVal <= 0) {
        showToast(t(`message:MS050034`), 'error')
        cancelRiderForm.setValue('reEstimatedInstallPremium', 0)
      }
    }
  }

  const changeEventForLA = (
    checkedAllLA: boolean,
    LAIndex: number,
    fieldItem: FieldArrayWithId<CancelRiderFormData.CancelAllRiderForm, 'LARiderForm', 'id'>
  ) => {
    if (checkedAllLA) {
      fieldItem.cancelRiderForm.forEach((subField, subIndex) => {
        const selectedElement = subField.SelectRowForRider
        if (!selectedElement) {
          cancelRiderForm.setValue(`LARiderForm.${LAIndex}.cancelRiderForm.${subIndex}.SelectRowForRider`, true)
          // reCalculateEstimatedInstallPremium(subField.installmentPrem, true)
          changeEventForRider(subField, true)
        }
      })
    } else {
      fieldItem.cancelRiderForm.forEach((subField, subIndex) => {
        const selectedElement = subField.SelectRowForRider
        if (selectedElement) {
          cancelRiderForm.setValue(`LARiderForm.${LAIndex}.cancelRiderForm.${subIndex}.SelectRowForRider`, false)
          // reCalculateEstimatedInstallPremium(subField.installmentPrem, false)
          changeEventForRider(subField, false)
        }
      })
    }
  }

  const changeEventForRider = (riderInfo: CancelRiderFormData.RiderForm, isChecked: boolean) => {
    reCalculateEstimatedInstallPremium(riderInfo.installmentPrem, isChecked)
  }

  const onCancelAllRiderCheckedEvent = (value: boolean) => {
    cancelRiderFormArray.fields.map((fieldItem, index) => {
      cancelRiderForm.setValue(`LARiderForm.${index}.selectAllForLA`, value)
      changeEventForLA(value, index, fieldItem)
    })
  }

  const isCheckedValidRider = () => {
    let invalidFlag: boolean = false
    cancelRiderFormArray.fields.forEach((fieldItem) => {
      fieldItem.cancelRiderForm.forEach((riderItem) => {
        if (riderItem.SelectRowForRider) {
          invalidFlag = true
        }
      })
    })
    return invalidFlag
  }

  const validateSubmitData = () => {
    const ReEstimatePremium = cancelRiderForm.watch('reEstimatedInstallPremium')
    if (!isCheckedValidRider()) {
      setErrorMsg(t('message:MS050234'))
      return false
    } else if (ReEstimatePremium < Number(detailData?.minPremium ?? 0)) {
      // showToast(t(`message:MS050014`), 'error')
      showToast(t(`message:MS050054`, { min: formatNumberWithComma(detailData?.minPremium ?? 0) }), 'error')
      return false
    } else if (ReEstimatePremium <= 0) {
      showToast(t(`message:MS050034`), 'error')
      return false
    } else if (['VLR9', 'ULR9', 'TLA9'].includes(detailData?.coverageCode || '')) {
      const hasRiderInvalidCondition1 = cancelRiderFormArray.fields
        .some((fieldItem) => fieldItem.cancelRiderForm
          .filter((rider) => rider.SelectRowForRider).some((rider) => {
            const isInvalidRider = pipe(
              O.fromNullable(
                customerReceivedDateOfRider.find((p) =>
                  !!p.customerReceivedDate &&
                  p.coverageCode === rider.riderCode &&
                  p.lifeNo === rider.lifeNo &&
                  p.coverageNo === rider.coverageNo &&
                  p.riderNo === rider.riderNo
                )
              ),
              O.fold(
                () => false,
                (receivedDateItem) => {
                  if (moment(rider.riskCommDate).valueOf() !== moment(detailData?.riskCommenceDate).valueOf()
                    && moment(receivedDateItem.tf57TransactionDate).isAfter(moment(effectiveDateDropRider))
                    && (moment(new Date()).diff(receivedDateItem.customerReceivedDate, 'days') <= 21)
                  ) {
                    return true
                  } else {
                    return false
                  }
                }
              )
            )
            return isInvalidRider
          }))
      const hasRiderInvalidCondition2 = cancelRiderFormArray.fields
        .some((fieldItem) => fieldItem.cancelRiderForm
          .filter((rider) => rider.SelectRowForRider).some((rider) => {
            const isInvalidRider = pipe(
              O.fromNullable(
                customerReceivedDateOfRider.find((p) =>
                  p.coverageCode === rider.riderCode &&
                  p.lifeNo === rider.lifeNo &&
                  p.coverageNo === rider.coverageNo &&
                  p.riderNo === rider.riderNo
                )
              ),
              O.fold(
                () => {
                  return false
                },
                (customerReceivedDateItem) => {
                  if (moment(rider.riskCommDate).valueOf() !== moment(detailData?.riskCommenceDate).valueOf()
                    && moment(customerReceivedDateItem.tf57TransactionDate).isAfter(moment(effectiveDateDropRider))
                    && customerReceivedDateItem.customerReceivedDate === ''
                  ) {
                    return true
                  } else {
                    return false
                  }
                }
              )
            )
            return isInvalidRider
          }))
      if (hasRiderInvalidCondition1) {
        showToast(t(`message:MS020123`), 'error')
        return false
      } else if (hasRiderInvalidCondition2) {
        showToast(t(`message:MS020122`), 'error')
        return false
      } else {
        setErrorMsg('')
        return true
      }
    } else {
      setErrorMsg('')
      return true
    }
  }

  const mappingData = (): CancelRider.SubmitCancelRiderData => {
    let mappingCancelRiderData: CancelRider.SubmitLARider[] = cancelRiderFormArray.fields.map((fieldItem) => {
      const riderListForEachLA: CancelRider.SubmitedRiderInfo[] = fieldItem.cancelRiderForm
        .filter((x) => x.SelectRowForRider)
        .map((subItem) => {
          const riskCommDate = convertDateStringForSubmit(subItem.riskCommDate)
          const riskCessDate = convertDateStringForSubmit(subItem.riskCessDate)
          const newInstallmentPremium = Number(detailData?.totalPremium ?? '') - subItem.installmentPrem
          return {
            productCode: subItem.riderCode,
            riskCommDate: riskCommDate,
            riskCessDate: riskCessDate,
            sumAssured: subItem.sumAssured,
            installmentPremium: subItem.installmentPrem,
            newInstallmentPremium: newInstallmentPremium,
            lifeNo: subItem.lifeNo,
            coverageNo: subItem.coverageNo,
            riderNo: subItem.riderNo,
            status: subItem.riderStatus
          }
        })
      return {
        lifeAssured: fieldItem.lifeAssured,
        riders: riderListForEachLA
      }
    })
    return {
      lifeAssureds: mappingCancelRiderData.filter((x) => !!x && x.riders.length > 0)
    }
  }

  props.initSubmission({
    validate: async () => {
      if (validateSubmitData()) {
        const submitedData = mappingData()
        // console.log(`submitedData: ${JSON.stringify(submitedData)}`)
        return {
          url: (policyNum: string) => `wf-api/policy/${policyNum}/cancel-rider/submission-for-staff`,
          body: submitedData,
          transactionName: RequestAuthenticateData.TransactionLabelShort(TransactionType.CANCEL_RIDER),
          collerationId: policyNum,
          transaction: TransactionType.CANCEL_RIDER
        }
      } else {
        return false
      }
    },
    clear: () => {
      cancelRiderForm.setValue('cancelAllRider', false)
      onCancelAllRiderCheckedEvent(false)
    }
  })

  return (
    <SafeAreaView style={{ flex: 1, marginTop: 15 }}>
      <View>
        <Title title={t('CancelRider:RIDERLIST')}></Title>
        <Controller
          control={cancelRiderForm.control}
          name="cancelAllRider"
          render={({ field: { onChange, onBlur, value } }) => (
            <Checkbox
              title={t('CancelRider:CancelAllRider')}
              value={value}
              onChange={(val) => {
                onChange(val)
                onCancelAllRiderCheckedEvent(val)
              }}
              onBlur={onBlur}
              // checkBoxStyle={{marginBottom:20}}
              disabled={props.isConfirmed}
            />
          )}
        />

        {cancelRiderFormArray.fields.map((fieldItem, index) => (
          <Panel title={fieldItem.LAName} key={fieldItem.id} isNoPadding={true} isTransactionToggleExpand={true}>
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', paddingRight: 20 }}>
              <Text style={{ fontStyle: 'italic' }}>{t('requestInfo:Currency')}: VND</Text>
            </View>
            {/* rider table */}
            <View style={{ flex: 1 }}>
              {/* table header */}
              <View>
                {/* table row */}
                <View style={{ flexDirection: 'row' }}>
                  <View style={{ flex: 5, padding: 10 }}>
                    <Controller
                      control={cancelRiderForm.control}
                      name={`LARiderForm.${index}.selectAllForLA`}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Checkbox
                          onChange={(checked) => {
                            onChange(checked)
                            changeEventForLA(checked, index, fieldItem)
                          }}
                          onBlur={onBlur}
                          value={value}
                          disabled={props.isConfirmed}
                          checkBoxStyle={cancelRiderStyles.tableCheckBox}
                        />
                      )}
                    />
                  </View>
                  <View style={{ flex: 25, padding: 10 }}>
                    <Text style={cancelRiderStyles.tableHeaderText}>{t('requestInfo:RiderName')}</Text>
                  </View>
                  <View style={{ flex: 20, padding: 10 }}>
                    <Text style={cancelRiderStyles.tableHeaderText}>{t('CancelRider:RiskCommencementDate')}</Text>
                  </View>
                  <View style={{ flex: 15, padding: 10 }}>
                    <Text style={cancelRiderStyles.tableHeaderText}>{t('requestInfo:RiskCessationDate')}</Text>
                  </View>
                  <View style={{ flex: 15, padding: 10 }}>
                    <Text style={cancelRiderStyles.tableHeaderText}>{t('requestInfo:SumAssured')}</Text>
                  </View>
                  <View style={{ flex: 20, padding: 10 }}>
                    <Text style={cancelRiderStyles.tableHeaderText}>{t('CancelRider:InstallmentPremium')}</Text>
                  </View>
                </View>
              </View>
              {/* table body */}
              <View>
                {fieldItem.cancelRiderForm.map((riderInfo, subIndex) => {
                  return (
                    <View
                      key={subIndex}
                      style={{ flexDirection: 'row', borderTopWidth: 1, borderTopColor: 'rgb(238, 238, 238)' }}
                    >
                      <View style={{ flex: 5, padding: 10 }}>
                        <Controller
                          control={cancelRiderForm.control}
                          name={`LARiderForm.${index}.cancelRiderForm.${subIndex}.SelectRowForRider`}
                          render={({ field: { onChange, onBlur, value } }) => (
                            <Checkbox
                              onChange={(riderCheck) => {
                                onChange(riderCheck)
                                changeEventForRider(riderInfo, riderCheck)
                              }}
                              onBlur={onBlur}
                              value={value}
                              disabled={props.isConfirmed}
                              checkBoxStyle={cancelRiderStyles.tableCheckBox}
                            />
                          )}
                        />
                      </View>
                      <View style={{ flex: 25, padding: 10 }}>
                        <Text>{riderInfo.riderName}</Text>
                      </View>
                      <View style={{ flex: 20, padding: 10 }}>
                        <Text>{convertStringToDateString(riderInfo.riskCommDate)}</Text>
                      </View>
                      <View style={{ flex: 15, padding: 10 }}>
                        <Text>{convertStringToDateString(riderInfo.riskCessDate)}</Text>
                      </View>
                      <View style={{ flex: 15, padding: 10 }}>
                        <Text>{convertNumberToCurrencyFormat(riderInfo.sumAssured)}</Text>
                      </View>
                      <View style={{ flex: 20, padding: 10 }}>
                        <Text>{convertNumberToCurrencyFormat(riderInfo.installmentPrem)}</Text>
                      </View>
                    </View>
                  )
                })}
              </View>
            </View>
          </Panel>
        ))}
        <View>
          {!props.isConfirmed && (
            <RoundedButton
              showBorder={false}
              filled={true}
              activeOpacity={0.7}
              text={t('CancelRider:CalculatePremium')}
              textStyle={cancelRiderStyles.btnText}
              style={{ height: 39, paddingHorizontal: 20 }}
              textColorDisable="#FFFFFF"
              textColorEnable="#FFFFFF"
              bgColorDisable={'#B0B0B0'}
              bgColorEnable={'#ED1B2E'}
              borderColorDisable={'#B0B0B0'}
              borderColorEnable={'#ED1B2E'}
              onPress={calculateEstimatedInstallPremium}
            />
          )}

          <View style={cancelRiderStyles.estimatedPremiumBtn}>
            <Text>{t('CancelRider:EstimatedNewInstallmentPremium')}</Text>
            {cancelRiderForm.watch('reEstimatedInstallPremium') > 0 && (
              <Text style={cancelRiderStyles.red_line}>
                {convertNumberToCurrencyFormat(cancelRiderForm.watch('reEstimatedInstallPremium'))} VND
              </Text>
            )}
          </View>
          <View style={cancelRiderStyles.estimatedPremiumBtn}>{!!errorMsg && <Error message={errorMsg} />}</View>
        </View>
      </View>
    </SafeAreaView>
  )
}
const cancelRiderStyles = StyleSheet.create({
  btnText: {
    fontSize: 14
  },
  red_line: {
    color: '#ed1c2e',
    fontSize: 15,
    fontWeight: '700'
  },
  estimatedPremiumBtn: {
    marginTop: 15
  },
  tableHeaderText: {
    fontSize: 15,
    fontWeight: '700'
  },
  TableColText: {
    fontSize: 15
  },
  tableCheckBox: {
    padding: 0,
    marginTop: 0,
    paddingLeft: 10
  }
})
